import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@chakra-ui/react3'
import {useStyles} from './context'
import Heading from '../../components/cms/heading'
import ButtonGroup from '../../components/cms/button-group'
import CTA from '../../components/cms/cta'
import PriceButtonGroup from '../../components/cms/price-button-group'
import Copy from '../../components/cms/copy'
import Fade from '../../components/cms/fade'

/**
 * Hero content with heading, copy, and buttons
 *
 * @component
 * @param {number} [headingLevel] - HTML heading level (1-6) for the main heading
 * @param {string} headline - Headline text
 * @param {object} [copy] - Copy object
 * @param {object} [primaryCta] - Primary call-to-action object
 * @param {object} [secondaryCta] - Secondary call-to-action object
 * @param {object} [priceProduct] - Price object with optional label
 * @returns {JSX.Element}
 */
const HeroContent = ({
    headingLevel = 2,
    headline,
    copy,
    primaryCta,
    secondaryCta,
    priceProduct,
    trackingContext
}) => {
    const styles = useStyles()
    const hasBothCtas = primaryCta?.label && secondaryCta?.label
    const hasPrice = priceProduct?.value != null
    const cta = useMemo(
        () =>
            primaryCta?.label
                ? {variant: 'primary', ...primaryCta}
                : {variant: 'secondary', ...secondaryCta},
        [primaryCta, secondaryCta]
    )

    return (
        <>
            <Box css={styles.textContainer}>
                <Fade>
                    <Heading
                        alignment={styles.heading.alignment}
                        headingLevel={headingLevel}
                        headline1={headline}
                        variant="hero"
                    />
                </Fade>
                {copy?.text && (
                    <Fade>
                        <Copy
                            {...copy}
                            alignment={styles.copy.alignment}
                            data-testid="hero-copy"
                            variant="hero"
                        />
                    </Fade>
                )}
            </Box>

            {/* Both CTAs or no price: render in ButtonGroup */}
            {hasBothCtas || !hasPrice ? (
                <ButtonGroup>
                    <CTA
                        variant="primary"
                        {...primaryCta}
                        trackingContext={{
                            ...trackingContext,
                            eventLabel: 'primaryCta'
                        }}
                    />
                    <CTA
                        {...secondaryCta}
                        variant="secondaryOnMedia"
                        trackingContext={{
                            ...trackingContext,
                            eventLabel: 'secondaryCta'
                        }}
                    />
                </ButtonGroup>
            ) : (
                // single/none cta with price
                <PriceButtonGroup
                    price={priceProduct}
                    cta={cta}
                    trackingContext={{...trackingContext, eventLabel: 'primaryCta'}}
                    variant="on-media"
                />
            )}
        </>
    )
}

HeroContent.propTypes = {
    copy: PropTypes.shape({
        text: PropTypes.string
    }),
    headline: PropTypes.string,
    headingLevel: PropTypes.number,
    priceProduct: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
        valueListPrice: PropTypes.number,
        label: PropTypes.string,
        hidePromotionSignals: PropTypes.bool
    }),
    primaryCta: PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.shape({
            type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
            value: PropTypes.string
        }),
        openInNewTab: PropTypes.bool
    }),
    secondaryCta: PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.shape({
            type: PropTypes.oneOf(['category', 'content', 'product', 'url', 'anchor']),
            value: PropTypes.string
        }),
        openInNewTab: PropTypes.bool
    }),
    trackingContext: PropTypes.shape({
        moduleName: PropTypes.string
    })
}

export default HeroContent
